import { bugsnagClient } from "@src/bugsnag";
import { isDev } from "@src/env";
import { useFeatureAccessChecks } from "./FeatureAccessContext";
import { AccessLevel, RoleBasedPermissionsFeature } from "./permissionsClaims";
import { useCallback, useMemo } from "react";
import { useFeatureFlag } from "@src/backpack-console/featureFlags";
import {
  QueryKey,
  useQuery,
  useMutation,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  DefaultError,
  NoInfer,
  SkipToken,
  UseMutationResult,
} from "@tanstack/react-query";

const ACCESS_LEVEL_INT = {
  read: 1,
  edit: 2,
} as const;
/**
 * Checks that feature access has been checked in an ancestor component and logs a warning if it hasn't or access isn't sufficient
 * When the `role-based-permissions` feature flag is on, returns the header for the BE to check feature access on the token.
 */
export default function usePreRequestFeatureAccessCheck(
  features: RoleBasedPermissionsFeature | RoleBasedPermissionsFeature[],
  accessLevel: AccessLevel,
  { superAdminOnly = false }: { superAdminOnly?: boolean } = {}
) {
  const checkFeatureAccess =
    useFeatureFlag("role-based-permissions", "off") === "v1";

  // this will be removed once the feature flag is removed
  const checkFeatureAccessHeader = useTemporaryCheckFeatureAccessHeader({
    checkFeatureAccess,
  });

  const accessChecks = useFeatureAccessChecks();

  const featuresList = useMemo(
    () => (Array.isArray(features) ? features : [features]),
    [features]
  );

  const checkedFeaturesAccessLevels = useMemo(
    () =>
      featuresList.filter((feature) =>
        [...accessChecks.keys()].includes(feature)
      ),
    [accessChecks, featuresList]
  );

  const hasEndpointAccess = useMemo(
    () =>
      superAdminOnly ||
      checkedFeaturesAccessLevels.some((feature) => {
        const _accessLevel = accessChecks.get(feature);
        if (!_accessLevel) return false;
        return ACCESS_LEVEL_INT[_accessLevel] >= ACCESS_LEVEL_INT[accessLevel];
      }),
    [accessChecks, accessLevel, checkedFeaturesAccessLevels, superAdminOnly]
  );

  const logInsufficientAccessWarning = useCallback(() => {
    if (!checkFeatureAccess) return;

    if (!checkedFeaturesAccessLevels.length && !superAdminOnly) {
      const message = `usePreRequestFeatureAccessCheck: expected ancestor component to have checked feature access for one of ${featuresList.join(
        ", "
      )} but none were checked`;
      logAccessCheckError(message);
      return;
    }

    if (checkedFeaturesAccessLevels.length && !hasEndpointAccess) {
      const message = `usePreRequestFeatureAccessCheck: ancestor component checked feature access for at least one of ${featuresList.join(
        ", "
      )} but access level was not ${accessLevel} or higher.`;
      logAccessCheckError(message);
    }
  }, [
    accessLevel,
    checkFeatureAccess,
    checkedFeaturesAccessLevels.length,
    featuresList,
    hasEndpointAccess,
    superAdminOnly,
  ]);

  return useMemo(
    () => ({ checkFeatureAccessHeader, logInsufficientAccessWarning }),
    [checkFeatureAccessHeader, logInsufficientAccessWarning]
  );
}

function logAccessCheckError(message: string) {
  if (isDev) {
    // eslint-disable-next-line
    console.warn(message);
  } else {
    bugsnagClient?.notify(message, (ev) => {
      ev.severity = "warning";
    });
  }
}

export const CHECK_FEATURE_ACCESS_HEADER_NAME = "X-Check-Feature-Access";
export type CheckFeatureAccessHeader =
  | {
      "X-Check-Feature-Access": string;
    }
  | {};
/**
 * The `"X-Check-Feature-Access"` header tells the service to evaluate permissions on
 * token claims - a value of `"false"` or not including the header
 * means the service will not check token claims permissions.
 */
export function useTemporaryCheckFeatureAccessHeader({
  checkFeatureAccess,
}: {
  checkFeatureAccess: boolean;
}) {
  return useMemo(
    () =>
      checkFeatureAccess
        ? {
            [CHECK_FEATURE_ACCESS_HEADER_NAME]: String(true),
          }
        : {},
    [checkFeatureAccess]
  ) satisfies CheckFeatureAccessHeader;
}

export function useQueryWithAccessCheckLog<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  [feature, access, superAdminOnly]: [
    feature: RoleBasedPermissionsFeature | RoleBasedPermissionsFeature[],
    access: AccessLevel,
    superAdminOnly?: boolean
  ],
  queryFn:
    | ((
        checkFeatureAccessHeader: CheckFeatureAccessHeader
      ) => Promise<TQueryFnData> | TQueryFnData)
    | SkipToken,
  options: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    "queryFn"
  >
): UseQueryResult<NoInfer<TData>, TError> {
  const { checkFeatureAccessHeader, logInsufficientAccessWarning } =
    usePreRequestFeatureAccessCheck(feature, access, { superAdminOnly });

  return useQuery({
    queryFn:
      typeof queryFn === "function"
        ? () => {
            logInsufficientAccessWarning();
            return queryFn(checkFeatureAccessHeader);
          }
        : queryFn,
    ...options,
  });
}

export function useMutationWithAccessCheckLog<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown
>(
  [feature, access, superAdminOnly]: [
    feature: RoleBasedPermissionsFeature | RoleBasedPermissionsFeature[],
    access: AccessLevel,
    superAdminOnly?: boolean
  ],
  mutationFn: (
    variables: TVariables,
    checkFeatureAccessHeader: CheckFeatureAccessHeader
  ) => Promise<TData>,
  options: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    "mutationFn"
  > = {}
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { checkFeatureAccessHeader, logInsufficientAccessWarning } =
    usePreRequestFeatureAccessCheck(feature, access, { superAdminOnly });
  return useMutation({
    mutationFn: (variables) => {
      logInsufficientAccessWarning();
      return mutationFn(variables, checkFeatureAccessHeader);
    },
    ...options,
  });
}
